import { ProductsService, PurchaseOrdersService } from '@/api/profile'
import { NavLink } from '@/app/v2/shared/components/layout/sidenav/sidenav'
import { ChangeDetectionStrategy, Component, HostListener, computed, inject, input, signal } from '@angular/core'
import { Router } from '@angular/router'
import { firstValueFrom } from 'rxjs'

type PaletteItem = {
  group: 'Navigation' | 'Articles' | 'Commandes'
  icon: string
  label: string
  sub?: string
  run: () => void
}

/**
 * Palette de commandes globale (Ctrl+K / ⌘K) : navigation vers n'importe quel écran + recherche
 * transverse (articles par désignation/référence, commandes/OT par n°) avec ouverture directe du
 * détail (deep-links ?productId= / ?id=). Surface compacte dédiée (pas la modale standard plein
 * écran — une palette se superpose légère, en haut de l'écran).
 */
@Component({
  selector: 'app-command-palette',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (open()) {
    <div class="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Palette de commandes">
      <div class="ui-overlay-backdrop absolute inset-0" (click)="close()"></div>

      <div class="absolute inset-x-0 top-[10%] flex justify-center px-4">
        <div class="ui-dialog-surface w-full max-w-xl overflow-hidden !rounded-2xl">
          <!-- Saisie -->
          <div class="flex items-center gap-2.5 border-b border-(--ui-border-split) px-4 py-3">
            <span class="material-icons ui-text-muted text-xl">search</span>
            <input
              #paletteInput
              type="text"
              [value]="query()"
              (input)="onQuery($event)"
              (keydown)="onKeydown($event)"
              placeholder="Aller à… ou rechercher un article, une commande, un OT"
              class="ui-input-native w-full bg-transparent text-[15px] outline-none"
              autocomplete="off"
              spellcheck="false"
            />
            @if (searching()) {
            <span class="material-icons ui-text-muted animate-spin text-base">autorenew</span>
            }
            <kbd class="ui-badge-accent rounded border px-1.5 py-0.5 font-mono text-[10px]">Échap</kbd>
          </div>

          <!-- Résultats -->
          <div class="max-h-[55vh] overflow-y-auto py-1.5">
            @for (group of groups(); track group.name) {
            <p class="ui-text-muted px-4 pb-1 pt-2 text-[10px] font-bold tracking-[0.14em] uppercase">{{ group.name }}</p>
            @for (item of group.items; track item.label + (item.sub ?? '')) {
            <button
              type="button"
              (click)="execute(item)"
              (mouseenter)="activeIndex.set(flatIndex(item))"
              [class]="
                'flex w-full items-center gap-3 px-4 py-2 text-left ' +
                (flatIndex(item) === activeIndex() ? 'ui-option-selected' : 'ui-row-hover')
              "
            >
              <span class="material-icons ui-text-muted text-lg">{{ item.icon }}</span>
              <span class="min-w-0 flex-1">
                <span class="ui-text-primary block truncate text-sm font-medium">{{ item.label }}</span>
                @if (item.sub) {
                <span class="ui-text-muted block truncate text-xs">{{ item.sub }}</span>
                }
              </span>
              @if (flatIndex(item) === activeIndex()) {
              <span class="material-icons ui-text-muted text-sm">keyboard_return</span>
              }
            </button>
            } }

            @if (groups().length === 0 && !searching()) {
            <p class="ui-text-muted px-4 py-6 text-center text-sm">
              @if (query().length < 2) { Tapez pour rechercher — articles, commandes, OT, écrans. } @else { Aucun résultat pour «
              {{ query() }} ». }
            </p>
            }
          </div>
        </div>
      </div>
    </div>
    }
  `,
})
export class CommandPalette {
  private readonly router = inject(Router)
  private readonly products = inject(ProductsService)
  private readonly orders = inject(PurchaseOrdersService)

  /** Liens de navigation (mêmes données que la sidenav). */
  readonly links = input<NavLink[]>([])

  readonly open = signal(false)
  readonly query = signal('')
  readonly searching = signal(false)
  readonly activeIndex = signal(0)

  private readonly productItems = signal<PaletteItem[]>([])
  private readonly orderItems = signal<PaletteItem[]>([])
  private searchTimer: ReturnType<typeof setTimeout> | null = null
  private searchSeq = 0

  /** Écrans à plat (liens directs + sous-menus). */
  private readonly navItems = computed<PaletteItem[]>(() => {
    const flat: PaletteItem[] = []
    for (const l of this.links()) {
      if (l.url) flat.push({ group: 'Navigation', icon: l.icon || 'chevron_right', label: l.name, run: () => void this.router.navigateByUrl(String(l.url)) })
      for (const n of l.nestedLinks ?? []) {
        if (n.url)
          flat.push({
            group: 'Navigation',
            icon: n.icon || 'chevron_right',
            label: `${l.name} · ${n.name}`,
            run: () => void this.router.navigateByUrl(String(n.url)),
          })
      }
    }
    return flat
  })

  readonly groups = computed(() => {
    const q = this.query().trim().toLowerCase()
    const nav = q.length === 0 ? this.navItems() : this.navItems().filter((i) => i.label.toLowerCase().includes(q))

    const groups: { name: string; items: PaletteItem[] }[] = []
    if (nav.length > 0) groups.push({ name: 'Navigation', items: nav.slice(0, 7) })
    if (this.productItems().length > 0) groups.push({ name: 'Articles', items: this.productItems() })
    if (this.orderItems().length > 0) groups.push({ name: 'Commandes & OT', items: this.orderItems() })
    return groups
  })

  private flatItems(): PaletteItem[] {
    return this.groups().flatMap((g) => g.items)
  }

  flatIndex(item: PaletteItem): number {
    return this.flatItems().indexOf(item)
  }

  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') {
      ev.preventDefault()
      this.open() ? this.close() : this.openPalette()
      return
    }
    if (ev.key === 'Escape' && this.open()) {
      ev.stopPropagation()
      this.close()
    }
  }

  openPalette(): void {
    this.open.set(true)
    this.query.set('')
    this.productItems.set([])
    this.orderItems.set([])
    this.activeIndex.set(0)
    // Focus après rendu (zoneless : le DOM est posé de façon synchrone après le set)
    setTimeout(() => document.querySelector<HTMLInputElement>('app-command-palette input')?.focus(), 0)
  }

  close(): void {
    this.open.set(false)
  }

  onQuery(ev: Event): void {
    const value = (ev.target as HTMLInputElement).value
    this.query.set(value)
    this.activeIndex.set(0)

    if (this.searchTimer) clearTimeout(this.searchTimer)
    const q = value.trim()
    if (q.length < 2) {
      this.productItems.set([])
      this.orderItems.set([])
      return
    }
    this.searchTimer = setTimeout(() => void this.runSearch(q), 250)
  }

  private async runSearch(q: string): Promise<void> {
    const seq = ++this.searchSeq
    this.searching.set(true)
    try {
      const [products, orders] = await Promise.all([
        firstValueFrom(this.products.searchProducts({ search: q, top: 5 })).catch(() => null),
        firstValueFrom(this.orders.searchPurchaseOrders({ search: q, top: 5, $expand: 'supplier' })).catch(() => null),
      ])
      if (seq !== this.searchSeq) return

      this.productItems.set(
        (products?.results ?? []).map((p) => ({
          group: 'Articles' as const,
          icon: 'category',
          label: p.supplierDesignation ?? p.supplierReference ?? '?',
          sub: [p.supplierReference, p.supplier?.displayName].filter(Boolean).join(' · '),
          run: () => void this.router.navigate(['/products'], { queryParams: { productId: p.id } }),
        })),
      )
      this.orderItems.set(
        (orders?.results ?? []).map((o) => ({
          group: 'Commandes' as const,
          icon: 'shopping_basket',
          label: o.additionalExternalId ?? '?',
          sub: o.supplier?.displayName ?? undefined,
          run: () => void this.router.navigate(['/orders'], { queryParams: { id: o.id } }),
        })),
      )
    } finally {
      if (seq === this.searchSeq) this.searching.set(false)
    }
  }

  onKeydown(ev: KeyboardEvent): void {
    const items = this.flatItems()
    if (ev.key === 'ArrowDown') {
      ev.preventDefault()
      this.activeIndex.update((i) => Math.min(items.length - 1, i + 1))
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault()
      this.activeIndex.update((i) => Math.max(0, i - 1))
    } else if (ev.key === 'Enter') {
      ev.preventDefault()
      const item = items[this.activeIndex()]
      if (item) this.execute(item)
    }
  }

  execute(item: PaletteItem): void {
    this.close()
    item.run()
  }
}
